import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Gem, 
  Rocket, 
  Gift, 
  Sparkles, 
  Zap, 
  Star,
  ArrowRight,
  Crown,
  Gamepad2,
  Brain,
  Coins,
  Clock,
  Flame,
  Target,
} from "lucide-react";
import { useSelector } from "react-redux";
import { UserModelData, PAGE_NAME } from "../../../types";
import useThemeHook from "../../hooks/useThemeHook";
import CommonPageTitle from "../../components/CommonPageTitle";

const TreasureVaultPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useThemeHook();
  const userData: UserModelData = useSelector((state: any) => state?.actualUser);
  const user = userData?.user;

  // Games that are visible directly on the page
  const featuredGames = [
    {
      id: "tap_rush",
      name: "Tap Rush",
      description: "Tap fast, earn ATH!",
      icon: <Zap size={24} className="text-yellow-400" />,
      reward: "1 ATH",
      color: "from-yellow-500 to-orange-500",
      route: PAGE_NAME.TAP_RUSH,
    },
    {
      id: "memory_match",
      name: "Memory Match",
      description: "Test your memory",
      icon: <Brain size={24} className="text-purple-400" />,
      reward: "2 ATH",
      color: "from-purple-500 to-pink-500",
      route: PAGE_NAME.MEMORY_MATCH,
    },
  ];

  // Pi-spending features (the real revenue drivers)
  const spendingFeatures = [
    {
      id: "forge",
      title: "🔥 Arthereum Forge",
      subtitle: "BOOST YOUR EARNINGS",
      description: "Multiply your mining rewards up to 3x!",
      price: "From 25 π",
      urgency: "Limited slots today",
      icon: <Rocket size={28} className="text-amber-400" />,
      bgGradient: "from-amber-600/40 to-orange-600/40",
      borderColor: "border-amber-500/50",
      action: () => navigate(PAGE_NAME.FORGE),
      hot: true,
    },
    {
      id: "mystery",
      title: "🎁 Mystery Box",
      subtitle: "JACKPOT AWAITS",
      description: "Win up to 50 π or rare boosts!",
      price: "From 10 π",
      urgency: "3 boxes left at this price",
      icon: <Gift size={28} className="text-purple-400" />,
      bgGradient: "from-purple-600/40 to-pink-600/40",
      borderColor: "border-purple-500/50",
      action: () => navigate(PAGE_NAME.MYSTERY_BOX),
    },
    {
      id: "pro",
      title: "👑 Go PRO",
      subtitle: "2X EVERYTHING",
      description: "Double earnings, no ads, elite badge",
      price: "500 π/month",
      urgency: "Early bird pricing ends soon",
      icon: <Crown size={28} className="text-yellow-400" />,
      bgGradient: "from-yellow-600/40 to-amber-600/40",
      borderColor: "border-yellow-500/50",
      action: () => navigate(PAGE_NAME.PRO),
      isPro: user?.isPro || user?.isLifetimePro,
    },
  ];

  return (
    <div className={`min-h-screen pb-24 ${theme === "dark" ? "bg-slate-900" : "bg-gray-50"}`}>
      <CommonPageTitle title="Treasure Vault" padding={null} />
      
      <div className="max-w-lg mx-auto px-4 py-4">
        {/* Header with Balance - creates desire to have more */}
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className={`
            p-4 rounded-2xl mb-4 flex items-center justify-between
            ${theme === "dark" 
              ? "bg-gradient-to-r from-slate-800 to-slate-700 border border-slate-600" 
              : "bg-gradient-to-r from-white to-gray-50 border border-gray-200 shadow-lg"}
          `}
        >
          <div>
            <p className={`text-[10px] uppercase font-bold tracking-wider ${theme === "dark" ? "text-slate-400" : "text-gray-500"}`}>
              Your ATH Balance
            </p>
            <p className={`text-2xl font-black ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
              {(user?.arthereumBalance || 0).toFixed(2)} <span className="text-amber-500">ATH</span>
            </p>
          </div>
          <div className="text-right">
            <p className={`text-[10px] uppercase font-bold tracking-wider ${theme === "dark" ? "text-slate-400" : "text-gray-500"}`}>
              Pi Balance
            </p>
            <p className={`text-xl font-black ${theme === "dark" ? "text-cyan-400" : "text-cyan-600"}`}>
              {(user?.picoinBalance || 0).toFixed(2)} π
            </p>
          </div>
        </motion.div>

        {/* FREE GAMES Section - Hook them first */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Gamepad2 size={18} className="text-cyan-400" />
              <h2 className={`font-bold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                Free Games
              </h2>
              <span className="px-2 py-0.5 rounded-full bg-emerald-500 text-white text-[10px] font-bold">
                EARN ATH
              </span>
            </div>
            <button 
              onClick={() => navigate(PAGE_NAME.GAMES)}
              className={`text-xs font-bold flex items-center gap-1 ${theme === "dark" ? "text-cyan-400" : "text-cyan-600"}`}
            >
              See all <ArrowRight size={14} />
            </button>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {featuredGames.map((game, idx) => (
              <motion.button
                key={game.id}
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: idx * 0.1 }}
                onClick={() => navigate(game.route)}
                className={`
                  p-4 rounded-2xl text-left relative overflow-hidden
                  bg-gradient-to-br ${game.color}
                  hover:scale-105 transition-transform active:scale-95
                `}
              >
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="p-2 rounded-xl bg-white/20">
                      {game.icon}
                    </div>
                  </div>
                  <h3 className="font-bold text-white text-sm">{game.name}</h3>
                  <p className="text-white/70 text-[10px] mb-2">{game.description}</p>
                  <div className="flex items-center gap-1 text-white font-bold text-xs">
                    <Coins size={12} /> Win {game.reward}
                  </div>
                </div>
                <div className="absolute -right-4 -bottom-4 opacity-20">
                  <Gamepad2 size={80} />
                </div>
              </motion.button>
            ))}
          </div>
        </div>

        {/* SPENDING Features - The money makers with psychological triggers */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 mb-2">
            <Flame size={18} className="text-orange-500" />
            <h2 className={`font-bold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
              Power Up Your Earnings
            </h2>
          </div>

          {spendingFeatures.map((feature, idx) => (
            <motion.div
              key={feature.id}
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 + idx * 0.1 }}
              onClick={feature.action}
              className={`
                relative overflow-hidden rounded-2xl p-4 cursor-pointer
                bg-gradient-to-r ${feature.bgGradient}
                border-2 ${feature.borderColor}
                ${theme === "dark" ? "bg-slate-800/80" : "bg-white/90"}
                hover:scale-[1.02] transition-transform active:scale-98
              `}
            >
              {/* Hot Badge */}
              {feature.hot && (
                <div className="absolute top-2 right-2">
                  <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-gradient-to-r from-red-500 to-orange-500 text-white animate-pulse">
                    🔥 HOT
                  </span>
                </div>
              )}
              
              {/* Active Pro Badge */}
              {feature.isPro && (
                <div className="absolute top-2 right-2">
                  <span className="px-2 py-1 rounded-full text-[10px] font-bold bg-emerald-500 text-white">
                    ✓ ACTIVE
                  </span>
                </div>
              )}

              <div className="flex items-start gap-3">
                <div className={`p-3 rounded-xl shrink-0 ${theme === "dark" ? "bg-slate-700/50" : "bg-white/50"}`}>
                  {feature.icon}
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-bold text-amber-400 uppercase tracking-wider mb-0.5">
                    {feature.subtitle}
                  </p>
                  <h3 className={`font-bold text-base ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                    {feature.title}
                  </h3>
                  <p className={`text-xs ${theme === "dark" ? "text-slate-400" : "text-gray-500"} mb-2`}>
                    {feature.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-black ${theme === "dark" ? "text-cyan-400" : "text-cyan-600"}`}>
                      {feature.price}
                    </span>
                    <span className="text-[10px] text-red-400 font-bold animate-pulse">
                      ⏰ {feature.urgency}
                    </span>
                  </div>
                </div>
                
                <ArrowRight size={18} className={theme === "dark" ? "text-slate-500" : "text-gray-400"} />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Lucky Spin - One more free hook */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
          onClick={() => navigate(PAGE_NAME.LUCKY_SPIN)}
          className={`
            mt-4 p-4 rounded-2xl cursor-pointer flex items-center justify-between
            bg-gradient-to-r from-blue-600/30 to-cyan-600/30 border border-blue-500/30
            hover:scale-[1.02] transition-transform active:scale-98
          `}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-blue-500/20">
              <Star size={24} className="text-blue-400" />
            </div>
            <div>
              <h3 className={`font-bold ${theme === "dark" ? "text-white" : "text-gray-900"}`}>
                Lucky Spin 🎰
              </h3>
              <p className={`text-xs ${theme === "dark" ? "text-blue-300" : "text-blue-600"}`}>
                Free daily spin for rewards!
              </p>
            </div>
          </div>
          <div className="px-3 py-1 rounded-full bg-blue-500 text-white text-xs font-bold">
            FREE
          </div>
        </motion.div>

        {/* Pro Tip - Plant the seed for Pro upgrade */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.6 }}
          className={`
            mt-4 p-3 rounded-2xl text-center
            ${theme === "dark" ? "bg-indigo-900/30 border border-indigo-500/20" : "bg-indigo-50 border border-indigo-200"}
          `}
        >
          <div className="flex items-center justify-center gap-2 mb-1">
            <Target size={14} className="text-indigo-400" />
            <span className={`text-xs font-bold ${theme === "dark" ? "text-indigo-300" : "text-indigo-600"}`}>
              Pro Tip
            </span>
          </div>
          <p className={`text-[11px] ${theme === "dark" ? "text-slate-400" : "text-gray-500"}`}>
            Boost purchases + Pro membership = <span className="font-bold text-emerald-400">6x earnings!</span>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default TreasureVaultPage;
